import { z } from 'zod';
import { CUSTOMER_IMPORT_LIMITS, ImportMappingSchema } from '../customer-import.js';

/**
 * Import des clients et véhicules (08/10/2026). Le navigateur décode le fichier (UTF-8 ou Windows-1252)
 * et envoie le **texte** ; l'API refait la lecture, la correspondance et toutes les validations.
 */
export const ImportDuplicateMode = z.enum(['update', 'skip']);
export type ImportDuplicateMode = z.infer<typeof ImportDuplicateMode>;

export const CustomerImportInput = z.strictObject({
  content: z.string().min(1).max(CUSTOMER_IMPORT_LIMITS.maxChars),
  delimiter: z.enum([';', ',', '\t']).optional(),
  /** Absente : correspondance proposée par le serveur (`suggestMapping`). */
  mapping: ImportMappingSchema.optional(),
  /** Client ou véhicule déjà présent dans le garage : mis à jour ou ignoré (choix explicite). */
  onDuplicate: ImportDuplicateMode,
  /** Plaque non française acceptée comme plaque étrangère ou spéciale. */
  foreignPlates: z.boolean().default(false),
});
export type CustomerImportInput = z.input<typeof CustomerImportInput>;

export const ImportRowStatus = z.enum(['create', 'update', 'skip', 'error']);
export type ImportRowStatus = z.infer<typeof ImportRowStatus>;

export const ImportCounts = z.object({
  create: z.number().int(),
  update: z.number().int(),
  skip: z.number().int(),
  error: z.number().int(),
});

export const CustomerImportPreviewOutput = z.object({
  delimiter: z.enum([';', ',', '\t']),
  headers: z.array(z.string()),
  mapping: ImportMappingSchema,
  totalRows: z.number().int(),
  /** Résultat prévu pour tout le fichier (rien n'est enregistré). */
  counts: ImportCounts,
  /** Les `CUSTOMER_IMPORT_LIMITS.previewRows` premières lignes. */
  rows: z.array(
    z.object({
      /** Numéro de ligne dans le fichier (en-tête = 1). */
      line: z.number().int(),
      status: ImportRowStatus,
      name: z.string().nullable(),
      plate: z.string().nullable(),
      vehicle: z.string().nullable(),
      phone: z.string().nullable(),
      email: z.string().nullable(),
      errors: z.array(z.string()),
      /** Pourquoi la ligne est ignorée ou mise à jour (ex. « Plaque déjà enregistrée »). */
      reason: z.string().nullable(),
    }),
  ),
});
export type CustomerImportPreviewOutput = z.infer<typeof CustomerImportPreviewOutput>;

export const CustomerImportReportOutput = z.object({
  totalRows: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  errors: z.number().int(),
  /** Clients et véhicules réellement créés (une ligne peut créer les deux). */
  customersCreated: z.number().int(),
  vehiclesCreated: z.number().int(),
  headers: z.array(z.string()),
  /** Lignes en erreur, avec leurs cellules d'origine (fichier CSV des erreurs construit par l'interface). */
  errorRows: z.array(z.object({ line: z.number().int(), cells: z.array(z.string()), errors: z.array(z.string()) })),
});
export type CustomerImportReportOutput = z.infer<typeof CustomerImportReportOutput>;
